import type { FormEvent } from "react";
import { ChevronLeft, Lock, RefreshCw } from "lucide-react";
import { FileTypeIcon } from "../FileTypeIcon";
import { Logo } from "../Logo";
import { BrowseFolderButton } from "../BrowseFolderButton";
import { ChatList } from "../ChatList";
import { WorkbenchRailPanel } from "../WorkbenchRailPanel";
import type { FileEntry, AgentSession, ProjectRecord, RecentFolderRecord } from "../../types";
import type { MaxMode } from "./types";
import { sessionsStore } from "../../stores/sessionsStore";
import { chatsStore } from "../../stores/chatsStore";
import type { AgentProfile } from "../../types";

type Surface = "explorer" | "search" | "sessions" | "agents" | "workspaces";
type LeftTab = "chats" | "sessions";

interface ActiveRoot {
  projectId: string | null;
  rootPath: string;
  name: string;
}

export interface SessionRailProps {
  surface: Surface;
  onSetSessionCollapsed: (value: boolean) => void;
  activeRoot: ActiveRoot | null;
  files: {
    projects: ProjectRecord[];
    recentFolders: RecentFolderRecord[];
    rootPath: string | null;
    selectedProjectId: string | null;
    error: string | null;
  };
  projectPanelOpen: boolean;
  onSetProjectPanelOpen: (updater: boolean | ((prev: boolean) => boolean)) => void;
  projectNameDraft: string;
  onSetProjectNameDraft: (value: string) => void;
  projectRootDraft: string;
  onSetProjectRootDraft: (value: string) => void;
  onCreateProject: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onBrowseProjectRootOnce: () => Promise<void>;
  onActivateProject: (projectId: string | null, rootPath: string, name: string) => void;
  onDeleteProject: (projectId: string) => Promise<void>;
  rootDraft: string;
  onSetRootDraft: (value: string) => void;
  onOpenRoot: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onBrowseFolder: (path: string) => void | Promise<void>;
  onRevealInFinder: () => void | Promise<void>;
  treeEntries: Map<string, FileEntry[]>;
  expandedDirs: Set<string>;
  onToggleDirectory: (path: string) => Promise<void>;
  onOpenFile: (entry: FileEntry) => Promise<void>;
  onRefreshTree: () => Promise<void>;
  onOpenSearch: (mode: "files" | "text") => void;
  workbenchActiveId: string | null;
  leftTab: LeftTab;
  onSetLeftTab: (tab: LeftTab) => void;
  sessions: {
    items: AgentSession[];
    loading: boolean;
  };
  activeSession: AgentSession | null;
  terminals: {
    agents: AgentProfile[];
    error: string | null;
  };
  onSetActivePane: (pane: string) => void;
  onSetChatVisible: (value: boolean) => void;
  onSetTerminalVisible: (value: boolean) => void;
  // Lets session-click reset a full-takeover pane (terminal / chat max) so
  // the editor + new session become visible. Optional so older callers
  // don't break, but WorkbenchView always supplies it.
  onSetMaxMode?: (mode: MaxMode) => void;
  customAgentPanelOpen: boolean;
  onSetCustomAgentPanelOpen: (updater: boolean | ((prev: boolean) => boolean)) => void;
  customAgent: { name: string; cmd: string; args: string; env: string };
  onSetCustomAgent: (value: { name: string; cmd: string; args: string; env: string }) => void;
  onAddCustomAgent: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onRemoveCustomAgent: (idOrName: string) => Promise<void>;
}

function FileTree({
  entries,
  expanded,
  treeEntries,
  onToggle,
  onOpen,
  depth = 0,
}: {
  entries: FileEntry[];
  expanded: Set<string>;
  treeEntries: Map<string, FileEntry[]>;
  onToggle: (path: string) => Promise<void>;
  onOpen: (entry: FileEntry) => Promise<void>;
  depth?: number;
}) {
  return (
    <ul className="files-tree__list">
      {entries.map((entry) => (
        <li key={entry.path} className="files-tree__item">
          <div
            className={`files-tree__row ${entry.restricted ? "is-restricted" : ""}`}
            style={{ paddingLeft: 4 + depth * 12 }}
            onClick={() =>
              entry.type === "directory" ? void onToggle(entry.path) : void onOpen(entry)
            }
          >
            <span className="files-tree__icon" aria-hidden="true">
              {entry.type === "directory" ? (
                expanded.has(entry.path) ? (
                  "v"
                ) : (
                  ">"
                )
              ) : entry.restricted ? (
                <Lock size={12} color="#facc15" />
              ) : (
                <FileTypeIcon path={entry.path} size={12} />
              )}
            </span>
            <span className="files-tree__label" title={entry.restrictedReason || entry.path}>
              {entry.name}
            </span>
          </div>
          {entry.type === "directory" &&
            expanded.has(entry.path) &&
            treeEntries.has(entry.path) && (
              <FileTree
                entries={treeEntries.get(entry.path) || []}
                expanded={expanded}
                treeEntries={treeEntries}
                onToggle={onToggle}
                onOpen={onOpen}
                depth={depth + 1}
              />
            )}
        </li>
      ))}
    </ul>
  );
}

export function SessionRail({
  surface,
  onSetSessionCollapsed,
  activeRoot,
  files,
  projectPanelOpen,
  onSetProjectPanelOpen,
  projectNameDraft,
  onSetProjectNameDraft,
  projectRootDraft,
  onSetProjectRootDraft,
  onCreateProject,
  onBrowseProjectRootOnce,
  onActivateProject,
  onDeleteProject,
  rootDraft,
  onSetRootDraft,
  onOpenRoot,
  onBrowseFolder,
  onRevealInFinder,
  treeEntries,
  expandedDirs,
  onToggleDirectory,
  onOpenFile,
  onRefreshTree,
  onOpenSearch,
  workbenchActiveId,
  leftTab,
  onSetLeftTab,
  sessions,
  activeSession,
  terminals,
  onSetActivePane,
  onSetChatVisible,
  onSetTerminalVisible,
  onSetMaxMode,
  customAgentPanelOpen,
  onSetCustomAgentPanelOpen,
  customAgent,
  onSetCustomAgent,
  onAddCustomAgent,
  onRemoveCustomAgent,
}: SessionRailProps) {
  return (
    <aside className="session-rail">
      <button
        className="rail-collapse"
        type="button"
        aria-label="Collapse sidebar"
        title="Collapse sidebar"
        onClick={() => onSetSessionCollapsed(true)}
      >
        <ChevronLeft />
      </button>
      <a className="brand" href="/" aria-label="Steerly Workbench">
        <div className="brand-mark" aria-hidden="true">
          <Logo />
        </div>
        <div>
          <strong>Steerly</strong>
          <span>Multi-agent coding</span>
        </div>
      </a>

      <section
        className="surface-panel"
        data-surface="workspaces"
        data-active={surface === "workspaces" ? "true" : undefined}
      >
        <h2 className="surface-panel__title">Workspaces</h2>
        <WorkbenchRailPanel />
      </section>

      <section
        className="surface-panel"
        data-surface="explorer"
        data-active={surface === "explorer" ? "true" : undefined}
      >
        <h2 className="surface-panel__title">Explorer</h2>
        <div className="rail-section">
          <div className="section-heading">
            <span>Project context</span>
            <button
              type="button"
              className="button secondary rail-action"
              onClick={() => onSetProjectPanelOpen((value) => !value)}
            >
              + Add
            </button>
          </div>
          <div className="project-active" data-show={activeRoot ? "true" : undefined}>
            {activeRoot ? `${activeRoot.projectId ? "Project" : "Folder"}: ${activeRoot.name}` : ""}
          </div>
          <div className="project-list">
            {files.projects.map((project) => (
              <button
                key={project.id}
                className="project-chip"
                data-active={project.id === files.selectedProjectId ? "true" : undefined}
                type="button"
                onClick={() =>
                  onActivateProject(project.id, project.rootPath, project.name || project.rootPath)
                }
              >
                <span className="project-chip__body">
                  <span className="project-chip__name">{project.name}</span>
                  <span className="project-chip__path">{project.rootPath}</span>
                </span>
                <span
                  className="project-chip__remove"
                  role="button"
                  tabIndex={-1}
                  onClick={(event) => {
                    event.stopPropagation();
                    void onDeleteProject(project.id);
                  }}
                >
                  x
                </span>
              </button>
            ))}
            {!files.projects.length && <p className="rail-empty">No saved projects yet.</p>}
          </div>
          {projectPanelOpen && (
            <form className="rail-expand rail-form" onSubmit={onCreateProject}>
              <input
                value={projectNameDraft}
                onChange={(event) => onSetProjectNameDraft(event.target.value)}
                type="text"
                placeholder="Project name"
                className="rail-input rail-input--text"
              />
              <div className="rail-form-row">
                <input
                  value={projectRootDraft}
                  onChange={(event) => onSetProjectRootDraft(event.target.value)}
                  type="text"
                  placeholder="/absolute/path/to/project"
                  spellCheck={false}
                  className="rail-input"
                  style={{ flex: 1 }}
                />
                <BrowseFolderButton onPick={(path) => onSetProjectRootDraft(path)} />
              </div>
              <div className="rail-form-row">
                <button className="button primary" type="submit" style={{ flex: 1 }}>
                  Save project
                </button>
                <button
                  className="button secondary"
                  type="button"
                  onClick={() => void onBrowseProjectRootOnce()}
                >
                  Use typed path
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="rail-section">
          <div className="section-heading">
            <span>File root</span>
          </div>
          <div className="project-list">
            {files.recentFolders.slice(0, 4).map((folder) => (
              <button
                key={folder.rootPath}
                className="project-chip project-chip--file-root"
                data-active={
                  !files.selectedProjectId && folder.rootPath === files.rootPath
                    ? "true"
                    : undefined
                }
                type="button"
                onClick={() =>
                  onActivateProject(null, folder.rootPath, folder.displayName || folder.rootPath)
                }
              >
                <span className="project-chip__body">
                  <span className="project-chip__name">
                    {folder.displayName || folder.rootPath}
                  </span>
                  <span className="project-chip__path">{folder.rootPath}</span>
                </span>
              </button>
            ))}
          </div>
          <form className="rail-form" onSubmit={onOpenRoot}>
            <div className="rail-form-row">
              <input
                value={rootDraft}
                onChange={(event) => onSetRootDraft(event.target.value)}
                type="text"
                placeholder="/absolute/path/to/browse"
                spellCheck={false}
                className="rail-input"
                style={{ flex: 1 }}
              />
              <BrowseFolderButton onPick={(path) => void onBrowseFolder(path)} />
            </div>
            <button
              className="button secondary rail-button-wide"
              type="button"
              onClick={() => void onRevealInFinder()}
              disabled={!files.selectedProjectId && !files.rootPath}
              title="Reveal the active folder in the file manager"
            >
              Open folder
            </button>
            {files.error && <p className="rail-error">{files.error}</p>}
          </form>
        </div>

        <div className="rail-section">
          <div className="section-heading">
            <span>Files</span>
            <button
              type="button"
              className="button secondary rail-action"
              onClick={() => void onRefreshTree()}
            >
              <RefreshCw size={12} />
            </button>
          </div>
          <div className="files-tree">
            {treeEntries.get("") ? (
              <FileTree
                entries={treeEntries.get("") || []}
                expanded={expandedDirs}
                treeEntries={treeEntries}
                onToggle={onToggleDirectory}
                onOpen={onOpenFile}
              />
            ) : (
              <p className="rail-empty">
                {activeRoot ? "Loading..." : "Choose a file root to browse files."}
              </p>
            )}
          </div>
        </div>
      </section>

      <section
        className="surface-panel"
        data-surface="search"
        data-active={surface === "search" ? "true" : undefined}
      >
        <h2 className="surface-panel__title">Search</h2>
        <div className="rail-section">
          <button
            className="button primary"
            type="button"
            style={{ width: "100%" }}
            onClick={() => onOpenSearch("files")}
          >
            Find files (Cmd+P)
          </button>
        </div>
        <div className="rail-section">
          <button
            className="button secondary"
            type="button"
            style={{ width: "100%" }}
            onClick={() => onOpenSearch("text")}
          >
            Find in files (Cmd+Shift+F)
          </button>
        </div>
        <p className="rail-hint">
          Restricted files (.env, keys, .ssh) are excluded from both search modes. Every search is
          audited.
        </p>
      </section>

      <section
        className="surface-panel"
        data-surface="sessions"
        data-active={surface === "sessions" ? "true" : undefined}
      >
        <h2 className="surface-panel__title">Chats</h2>
        <ChatList
          workbenchId={workbenchActiveId || "wb-default"}
          onSelect={() => {
            // Selecting (or creating) a chat reveals the ChatPanel and
            // dismisses the full-surface terminal overlay so the chat shows.
            onSetActivePane("chat");
            onSetChatVisible(true);
            onSetTerminalVisible(false);
            onSetMaxMode?.("none");
          }}
        />
      </section>

      <section
        className="surface-panel"
        data-surface="agents"
        data-active={surface === "agents" ? "true" : undefined}
      >
        <h2 className="surface-panel__title">Agents</h2>
        <div className="rail-section">
          <div className="section-heading">
            <span>Custom agents</span>
            <button
              type="button"
              className="button secondary rail-action"
              onClick={() => onSetCustomAgentPanelOpen((value) => !value)}
            >
              Manage
            </button>
          </div>
          <div className="agent-manage__list">
            {terminals.agents
              .filter((agent) => agent.custom)
              .map((agent) => (
                <div key={agent.name} className="connector">
                  <div>
                    <strong>{agent.name}</strong>
                    <span>{agent.cmd}</span>
                  </div>
                  <button
                    className="button secondary"
                    type="button"
                    onClick={() =>
                      void onRemoveCustomAgent(
                        String((agent as unknown as Record<string, unknown>).id || agent.name)
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
            {!terminals.agents.some((agent) => agent.custom) && (
              <p className="rail-empty">No custom agents configured.</p>
            )}
          </div>
          {customAgentPanelOpen && (
            <form className="agent-manage__form rail-form" onSubmit={onAddCustomAgent}>
              <input
                value={customAgent.name}
                onChange={(event) => onSetCustomAgent({ ...customAgent, name: event.target.value })}
                type="text"
                placeholder="Display name"
                required
                className="rail-input rail-input--text"
              />
              <input
                value={customAgent.cmd}
                onChange={(event) => onSetCustomAgent({ ...customAgent, cmd: event.target.value })}
                type="text"
                placeholder="Binary"
                required
                spellCheck={false}
                className="rail-input"
              />
              <input
                value={customAgent.args}
                onChange={(event) => onSetCustomAgent({ ...customAgent, args: event.target.value })}
                type="text"
                placeholder="Arguments"
                spellCheck={false}
                className="rail-input"
              />
              <textarea
                value={customAgent.env}
                onChange={(event) => onSetCustomAgent({ ...customAgent, env: event.target.value })}
                rows={3}
                placeholder="Optional env, KEY=VALUE per line"
                spellCheck={false}
                className="rail-textarea"
              />
              <button className="button primary" type="submit">
                Add agent
              </button>
              {terminals.error && <p className="rail-error">{terminals.error}</p>}
            </form>
          )}
        </div>
      </section>
    </aside>
  );
}
